import React, { useState } from "react";
import {
    Box,
    Header,
    Pagination,
    SpaceBetween,
    Table,
    TextFilter,
} from "@cloudscape-design/components";
import {
    recommendedVersionCell,
    riskStatusCell,
    vendorGuidanceStatusCell,
} from "./cells";
import { compareByRisk, DependencyTableRow, filterableText } from "./helpers";
import { RiskLevelsInfoPopover } from "./popovers";

export interface DependencyTableProps {
    items: DependencyTableRow[];
    headerTitle: React.ReactNode;
    showMajorVersionUpgradeInfo?: boolean;
    headerActions?: React.ReactNode;
    loading?: boolean;
    loadingText?: string;
    emptyText?: string;
    filteringPlaceholder?: string;
    pageSize?: number;
}

export const DependencyTable: React.FC<DependencyTableProps> = ({
    items,
    headerTitle,
    showMajorVersionUpgradeInfo = false,
    headerActions,
    loading = false,
    loadingText = "Loading resources",
    emptyText = "No dependency found",
    filteringPlaceholder = "Filter dependencies",
    pageSize = 50,
}) => {
    const [filteringText, setFilteringText] = useState("");
    const [currentPageIndex, setCurrentPageIndex] = useState(1);

    const filteredItems = items
        .filter(
            (row) =>
                !filteringText ||
                filterableText(row).includes(filteringText.toLowerCase())
        )
        .sort(compareByRisk);

    const paginatedItems = filteredItems.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
    );

    const totalPages = Math.ceil(filteredItems.length / pageSize);

    return (
        <Table
            items={paginatedItems}
            columnDefinitions={[
                {
                    id: "dependencyName",
                    header: "Dependency Name",
                    cell: (e: DependencyTableRow) => e.dependencyName || "-",
                },
                {
                    id: "version",
                    header: "Current Version",
                    cell: (e: DependencyTableRow) => e.version || "-",
                },
                {
                    id: "vendorGuidanceStatus",
                    header: "Current Version Status",
                    cell: (e: DependencyTableRow) =>
                        vendorGuidanceStatusCell(e.vendorGuidanceStatus),
                },
                {
                    id: "recommendedVersion",
                    header: "Recommended Version",
                    cell: (e: DependencyTableRow) =>
                        recommendedVersionCell(e, showMajorVersionUpgradeInfo),
                },
                {
                    id: "riskStatus",
                    header: (
                        <SpaceBetween direction="horizontal" size="xs">
                            <span>Risk</span>
                            <RiskLevelsInfoPopover />
                        </SpaceBetween>
                    ),
                    cell: (e: DependencyTableRow) => riskStatusCell(e.riskStatus),
                },
            ]}
            resizableColumns
            stickyHeader
            renderAriaLive={({ firstIndex, lastIndex, totalItemsCount }) =>
                `Displaying items ${firstIndex} to ${lastIndex} of ${totalItemsCount}`
            }
            loading={loading}
            loadingText={loadingText}
            empty={
                <Box margin={{ vertical: "xs" }} textAlign="center" color="inherit">
                    <SpaceBetween size="m">
                        <b>{emptyText}</b>
                    </SpaceBetween>
                </Box>
            }
            filter={
                <TextFilter
                    filteringPlaceholder={filteringPlaceholder}
                    filteringText={filteringText}
                    onChange={({ detail }) => {
                        setFilteringText(detail.filteringText);
                        setCurrentPageIndex(1);
                    }}
                    countText={`${filteredItems.length} matches`}
                />
            }
            header={
                <Header
                    counter={`(${filteredItems.length})`}
                    actions={headerActions}
                >
                    {headerTitle}
                </Header>
            }
            pagination={
                <Pagination
                    currentPageIndex={currentPageIndex}
                    pagesCount={totalPages}
                    onChange={({ detail }) =>
                        setCurrentPageIndex(detail.currentPageIndex)
                    }
                />
            }
        />
    );
};

export default DependencyTable;
